import React, { ReactNode, useEffect, useId, useMemo, useState } from "react";
import styles from "./settings-page.module.css";
import homeStyles from "../portal.module.css";
import {
	CoreButton,
	CoreCard,
	CoreCheckbox,
	CoreRadioGroup,
	CoreSpinner,
	CoreTextBox,
	coreToast,
	RadioOption,
} from "@verity/components/core";
import { ScreenReaderText } from "@verity/components";
import { error403, error500 } from "../shared";
import i18n from "../../i18n";
import apiClient from "../../helpers/api-client";
import { Box, SvgIconProps, Typography } from "@mui/material";
import VisualizerBanner from "./visualizer-banner";

type InputType = "boolean" | "number" | "string" | "checkbox" | "custom";

type VeritySettingBase = {
	settingName: string;
	settingTitle: string;
	settingDescription?: ReactNode;
	// eslint-disable-next-line deprecation/deprecation
	icon?:
		| React.FunctionComponent<React.SVGProps<SVGSVGElement>>
		| React.ReactNode
		| ((props: SvgIconProps) => JSX.Element);
	iconStroke?: boolean;
	inputText?: string[];
	inputInfoDescription?: (settingsInfo?: CustomSettingData["options"]) => React.ReactNode;
};

/**
 * @property `settingName` is a unique key containing the type of setting and a setting tag.
 * @property `settingTitle` is a string placed in the setting card header.
 * @property `settingDescription` is a string that describes the setting, placed below the setting card header.
 * @property `icon` is a unique key containing the type of setting and a setting description.
 * @property `inputText` is an array of strings that replaces the default input text, each entry replaces a single text.
 * @property `inputInfoDescription` outputs an additional description below the setting description based on the `settingsInfo` input.
 * @property `type` is the type of input that sets the setting value. If `type` is `"custom"`, `customSetting` must also be provided.
 * @property `customSetting` contains `component`, `loadFunc`, `saveFunc`, and `validator` to process a setting that does not match the input types provided in `type`.
 */
export type VeritySetting<T = void, R = void> =
	| (VeritySettingBase & { type: Exclude<InputType, "custom"> })
	| (VeritySettingBase & { type: Extract<InputType, "custom">; customSetting: CustomSetting<T, R> });

export type VeritySettingGroup = VeritySetting<any, any>[];

/**
 * @property `component` is the component through which a user may change the setting value and/or options.
 * @property `loadFunc` is called upon load and passes the response to `component`.
 * @property `saveFunc` takes the current setting value and settings options and saves.
 * @property `validator` is called to validate the type of the setting value upon saving.
 * @property `allowNullOrUndefinedValue` allows null or undefined values to be passed to the save function regardless of `validator`.
 */
export type CustomSetting<T, R> = {
	/**
	 * The component that will allow user input to change the setting value and/or options.
	 */
	component: (props: {
		loadData: R;
		setting: CustomSettingData<T>;
		settingsInfo: CustomSettingData["options"] | undefined;
		onSettingChange: (value?: T, options?: object) => void;
		disabled?: boolean;
		ariaLabelledByTitleId?: string;
	}) => React.ReactNode;
	/**
	 * Called upon load and passes the response to `component`.
	 */
	loadFunc?: () => Promise<R>;
	/**
	 * @param data The setting value.
	 * @param options Additional setting options that may be used during the save.
	 * @returns An object containing boolean `ok` for the success of the save,
	 * and optional string `reason` to describe the reason the save failed.
	 */
	saveFunc: (data?: T, options?: object) => Promise<SaveResponse>;
	/**
	 * Function called to validate the type of the setting value that will be used in `saveFunc`.
	 * @param obj The data that will be used in `saveFunc`.
	 */
	validator: (obj: unknown) => obj is T;
	/**
	 * if `true`, `null` or `undefined` setting values may be used in `saveFunc` regardless of `validator`.
	 */
	allowNullOrUndefinedValue?: boolean;
};

export type CustomSettingData<T = unknown> = {
	value?: T;
	options?: Record<string, unknown>;
};

type SettingInputProps = {
	settingName: string;
	activeSetting: string;
	handleInputChange: (value: string) => void;
	inputText?: string[];
	disabled?: boolean;
	titleAriaLabelledBy?: string;
};

export type SaveResponse = {
	ok: boolean;
	reason?: string;
};

const CheckboxInput = (props: SettingInputProps) => {
	const checkboxId = useId();

	return (
		<div style={{ display: "flex", gap: "28px", fontSize: "15px", color: "#525252" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.4rem",
					cursor: props.disabled ? "default" : "pointer",
				}}
				onClick={() => {
					if (props.disabled) return;
					props.handleInputChange(props.activeSetting === "true" ? "false" : "true");
				}}
			>
				<CoreCheckbox
					id={checkboxId}
					name={props.settingName}
					checked={props.activeSetting === "true"}
					readOnly
					onChange={() => props.handleInputChange(props.activeSetting === "true" ? "false" : "true")}
					disabled={props.disabled}
				/>
				<label htmlFor={checkboxId} style={{ cursor: props.disabled ? "default" : "pointer" }}>
					{props.inputText?.[0] ?? "Enabled"}
				</label>
			</div>
		</div>
	);
};

const BooleanInput = (props: SettingInputProps) => {
	const radioOptions: RadioOption[] = [
		{
			value: "true",
			label: props.inputText?.[0] ?? "Enabled",
			disabled: props.disabled,
		},
		{
			value: "false",
			label: props.inputText?.[1] ?? "Disabled",
			disabled: props.disabled,
		},
	];
	return (
		<div style={{ paddingLeft: "10px" }}>
			<CoreRadioGroup
				radioOptions={radioOptions}
				onChange={(e) => {
					props.handleInputChange(e.target.value);
				}}
				radioLabelFontSize={15}
				selectedValue={props.activeSetting}
				radioGroupAriaLabelledby={props.titleAriaLabelledBy}
			/>
		</div>
	);
};

const NumberInput = (props: SettingInputProps) => (
	<CoreTextBox
		type="number"
		name={props.settingName}
		height={32}
		width={"min(100%, 128)"}
		value={props.activeSetting}
		placeholder={props.inputText?.[0] ?? ""}
		onChange={(event) => {
			props.handleInputChange(event.target.value);
		}}
		disabled={props.disabled}
	/>
);

const StringInput = (props: SettingInputProps) => (
	<CoreTextBox
		type="text"
		name={props.settingName}
		height={32}
		width={"min(100%, 384px)"}
		value={props.activeSetting}
		placeholder={props.inputText?.[0] ?? ""}
		onChange={(event) => {
			props.handleInputChange(event.target.value);
		}}
		disabled={props.disabled}
	/>
);

type SettingsCardProps = {
	type: InputType;
	settingName: string;
	title: string;
	settingDescription?: ReactNode;
	activeSetting: string;
	onSettingChange: (name: string, value: string) => void;
	// eslint-disable-next-line deprecation/deprecation
	icon?:
		| React.FunctionComponent<React.SVGProps<SVGSVGElement>>
		| React.ReactNode
		| ((props: SvgIconProps) => JSX.Element);
	iconStroke?: boolean;
	inputText?: string[];
	settingsInfo?: CustomSettingData["options"];
	inputInfoDescription?: (settingsInfo?: CustomSettingData["options"]) => React.ReactNode;
	disabled?: boolean;
	customSetting?: CustomSetting<unknown, unknown>;
	customSettingLoad?: unknown;
	customSettingValue?: CustomSettingData;
	onCustomSettingChange: (name: string, value?: unknown, options?: object) => void;
};

const SettingCard = (props: SettingsCardProps) => {
	const [activeSetting, setActiveSetting] = useState<string>(props.activeSetting);

	function handleInputChange(value: string) {
		props.onSettingChange(props.settingName, value);
		setActiveSetting(value);
	}
	const titleAriaLabelledBy = useId();

	const cardProps = {
		title: props.title,
		titleAriaLabelledBy: titleAriaLabelledBy,
		description: props.settingDescription,
		icon: props.icon,
		iconStroke: props.iconStroke,
	};
	const inputProps: SettingInputProps = {
		settingName: props.settingName,
		activeSetting: activeSetting,
		handleInputChange: handleInputChange,
		inputText: props.inputText,
		disabled: props.disabled,
		titleAriaLabelledBy: titleAriaLabelledBy,
	};
	let input: React.ReactNode = null;

	switch (props.type) {
		case "boolean":
			input = <BooleanInput {...inputProps} />;
			break;
		case "checkbox":
			input = <CheckboxInput {...inputProps} />;
			break;
		case "number":
			input = <NumberInput {...inputProps} />;
			break;
		case "string":
			input = <StringInput {...inputProps} />;
			break;
		case "custom":
			if (props.customSetting && props.customSettingLoad && props.customSettingValue) {
				input = (
					<props.customSetting.component
						loadData={props.customSettingLoad}
						setting={props.customSettingValue}
						settingsInfo={props.settingsInfo}
						onSettingChange={(value, options) => props.onCustomSettingChange(props.settingName, value, options)}
						disabled={props.disabled}
						ariaLabelledByTitleId={titleAriaLabelledBy}
					/>
				);
			} else {
				input = null;
			}
			if (input === null) {
				return null;
			}
			break;
		default:
			return null;
	}

	return (
		<CoreCard {...cardProps} fontSize={15} lineHeight={1.5} useHeader>
			{props.inputInfoDescription && (
				<div className={styles.settingCardDescription}>{props.inputInfoDescription?.(props.settingsInfo)}</div>
			)}
			{input}
		</CoreCard>
	);
};

export type SettingsPageProps = {
	settingGroup: VeritySettingGroup;
	settingGroupName: string;
	hiddenH1?: string;
	loadFunc: (
		loader: (data: Record<string, string | unknown>) => void,
		customSettings?: Record<string, CustomSetting<unknown, unknown>>,
	) => Promise<boolean>;
	authFunc: () => Promise<boolean>;
	saveFunc: (
		data: Record<string, string | CustomSettingData>,
		customSettings?: Record<string, CustomSetting<unknown, unknown>>,
	) => Promise<SaveResponse>;
};

/**
 * @param propsWrapper contains `props` property requiring 6 inner properties:
 * @property `settingGroup` is a list of VeritySettingGroup that represents the types of settings for tab division.
 * @property `loadFunc` is an async Boolean function accepting a void function [to load all settings on the page given a `{[name: string]: string}` param of settings (any unfetched settings are considered inaccessible)] as parameter that completes any necessary loading procedures before the page becomes accessible. It returns whether loading was successful or not. It must handle its own errors.
 * @property `authFunc` is a parameterless async function that returns whether the accessing source is a Verity user. It must handle its own errors.
 * @property `saveFunc` is an async Boolean function accepting a single `{[name: string]: string}` parameter of settings that handles saving settings when the save button on the page is clicked. Returns whether saving succeeded or not. It must handle its own errors.
 */
const SettingsPage = (props: SettingsPageProps) => {
	const settingGroup = props.settingGroup;
	const settingGroupName = props.settingGroupName;
	const loadFunc = props.loadFunc;
	const authFunc = props.authFunc;
	const saveFunc = props.saveFunc;
	const customSettings = settingGroup.reduce((obj: Record<string, CustomSetting<unknown, unknown>>, x) => {
		if (x.type === "custom") {
			obj[x.settingName] = x.customSetting;
		}
		return obj;
	}, {});
	const customSettingNames = Object.keys(customSettings);
	const normalSettingNames = settingGroup.filter((x) => x.type !== "custom").map((x) => x.settingName);

	const [settingsLoaded, setLoaded] = useState<boolean | null>(null);
	const [authenticated, setAuthenticated] = useState<boolean | null>(null);
	const [activeSettings, setActiveSettings] = useState<Record<string, string>>({});
	const [settingsInfo, setSettingsInfo] = useState<CustomSettingData["options"]>({});

	const [disabled, setDisabled] = useState<boolean>(false);
	const [waiting, setWaiting] = useState<boolean>(false);
	const [customSettingsLoads, setCustomSettingsLoads] = useState<Record<string, unknown>>({});
	const [activeCustomSettings, setActiveCustomSettings] = useState<Record<string, CustomSettingData>>({});

	useEffect(() => {
		async function startUp() {
			if (!(await apiClient.authentication.isAuthenticated().catch(() => false))) {
				setAuthenticated(false);
				return;
			}
			const [loaded, authenticated] = await Promise.all([loadFunc(loadAllSettings, customSettings), authFunc()]);
			setLoaded(loaded);
			setAuthenticated(authenticated);
		}

		setWaiting(true);
		setActiveSettings({});
		setSettingsInfo({});
		setCustomSettingsLoads({});
		setActiveCustomSettings({});
		startUp()
			.catch(console.error)
			.finally(() => setWaiting(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.settingGroupName]);

	const InnerComponent = () => {
		if (settingsLoaded == null || authenticated == null) {
			return (
				<div style={{ display: "flex", alignItems: "center", placeContent: "center", height: "100%" }}>
					<CoreSpinner size={100} />
				</div>
			);
		}
		if (!authenticated) {
			return <iframe className={homeStyles.iframePage} src={`${error403}?auth=true`}></iframe>;
		}
		if (!settingsLoaded) {
			return <iframe className={homeStyles.iframePage} src={error500}></iframe>;
		}
		if (waiting) {
			return (
				<div style={{ display: "flex", alignItems: "center", placeContent: "center", height: "100%" }}>
					<CoreSpinner size={100} />
				</div>
			);
		}
		return (
			<div style={{ display: "flex", placeContent: "center", width: "100%" }}>
				{props.hiddenH1 && <ScreenReaderText component="h1">{props.hiddenH1}</ScreenReaderText>}
				<div className={styles.settings}>
					<VisualizerBanner />
					{settingsLoaded && Object.keys(activeSettings).length > 0 ? (
						<div style={{ display: "flex", gap: "10px", justifyContent: "end", width: "100%" }}>
							<CoreButton
								color="cancel"
								className={`${styles.settingsEditButton} `}
								onClick={() => void doResetAction()}
								disabled={disabled}
								aria-label="Discard Changes"
							>
								{i18n.t("portal.components.settingsPageTSX.discardChange")}
							</CoreButton>
							<CoreButton
								color="primary"
								className={`${styles.settingsEditButton} `}
								onClick={() => void doSaveAction()}
								disabled={disabled}
								aria-label="Save Changes"
							>
								{i18n.t("portal.components.settingsPageTSX.saveChange")}
							</CoreButton>
						</div>
					) : (
						<Box height="24px" />
					)}
					{settingGroupName === "thirdParty" && (
						<CoreCard
							style={{
								flex: 1,
								width: "100%",
								display: "flex",
								padding: "15px",
								boxSizing: "border-box",
								overflow: "hidden",
							}}
						>
							<Typography
								sx={{
									fontFamily: "Inter",
									fontWeight: 600,
									fontSize: 16,
									lineHeight: "100%",
									letterSpacing: 0,
									color: "#333333",
									paddingBottom: "10px",
								}}
							>
								{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.settingsBanner.title")}
							</Typography>

							<Typography
								sx={{
									fontFamily: "Inter",
									fontWeight: 400,
									fontSize: 14,
									lineHeight: "150%",
									letterSpacing: 0,
									color: "#333333",
								}}
							>
								{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.settingsBanner.description")}
							</Typography>
						</CoreCard>
					)}
					<div className={styles.settingCardWrapper} style={{ display: "flex" }}>
						{settingGroup.map((setting) => {
							if (activeSettings[setting.settingName] == null && setting.type !== "custom") {
								return null;
							}
							const settingProps = {
								type: setting.type,
								settingName: setting.settingName,
								title: setting.settingTitle,
								settingDescription: setting.settingDescription,
								activeSetting: activeSettings[setting.settingName],
								onSettingChange: settingChangeHandler,
								icon: setting.icon,
								iconStroke: setting.iconStroke,
								settingsInfo: settingsInfo,
								inputText: setting.inputText,
								inputInfoDescription: setting.inputInfoDescription,
								disabled: disabled,
							};
							const customProps = {
								customSetting: setting.type === "custom" ? setting.customSetting : undefined,
								customSettingLoad: customSettingsLoads[setting.settingName],
								customSettingValue: activeCustomSettings[setting.settingName],
								onCustomSettingChange: customSettingChangeHandler,
							};
							return <SettingCard key={setting.settingName} {...{ ...settingProps, ...customProps }} />;
						})}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className={styles.baseStyle}>
			{useMemo(() => {
				return <InnerComponent />;
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [settingsLoaded, authenticated, waiting])}
		</div>
	);

	function separateSettingsInfo(data: Record<string, string | unknown>) {
		const separatedData: {
			settings: Record<string, string>;
			info: Exclude<CustomSettingData["options"], undefined>;
			custom: Exclude<CustomSettingData["options"], undefined>;
		} = { settings: {}, info: {}, custom: {} };
		for (const [key, value] of Object.entries(data)) {
			if (key.includes("Info-")) {
				separatedData.info[key] = value;
			} else if (customSettingNames.includes(key)) {
				separatedData.custom[key] = value;
			} else if (typeof value === "string") {
				separatedData.settings[key] = value;
			}
		}
		return separatedData;
	}

	function loadAllSettings(data: Record<string, string | unknown>) {
		const separatedData = separateSettingsInfo(data);
		setActiveSettings(separatedData.settings);
		setSettingsInfo(separatedData.info);
		setCustomSettingsLoads(separatedData.custom);
		setActiveCustomSettings(customSettingNames.reduce((obj, x) => ({ ...obj, [x]: { value: undefined } }), {}));
	}

	function settingChangeHandler(name: string, value: string) {
		setActiveSettings((activeSetting) => {
			const newSettings: { [name: string]: string } = { ...activeSetting };
			newSettings[name] = value;
			return newSettings;
		});
	}

	function customSettingChangeHandler(name: string, value: unknown, options?: object) {
		const newSettings = { ...activeCustomSettings };
		newSettings[name].value = value;
		newSettings[name].options = { ...options };
		setActiveCustomSettings(newSettings);
	}

	async function doResetAction() {
		setWaiting(true);
		coreToast.dismiss();
		setLoaded(await loadFunc(loadAllSettings, customSettings));
		if (props.settingGroupName === "thirdParty") {
			coreToast.success(i18n.t("portal.components.settingsPageTSX.resetThirdParty"), {
				autoClose: 5000,
				draggable: true,
			});
		} else if (props.settingGroupName === "institute") {
			coreToast.success(i18n.t("portal.components.settingsPageTSX.resetInstitute"), {
				autoClose: 5000,
				draggable: true,
			});
		} else if (props.settingGroupName === "course") {
			coreToast.success(i18n.t("portal.components.settingsPageTSX.resetCourse"), {
				autoClose: 5000,
				draggable: true,
			});
		} else if (props.settingGroupName === "scope") {
			coreToast.success(i18n.t("portal.components.settingsPageTSX.resetScope"), {
				autoClose: 5000,
				draggable: true,
			});
		} else if (props.settingGroupName === "user") {
			coreToast.success(i18n.t("portal.components.settingsPageTSX.resetUser"), {
				autoClose: 5000,
				draggable: true,
			});
		}
		setWaiting(false);
	}

	function doSaveAction() {
		setActiveSettings((activeSetting) => {
			void (async () => {
				setDisabled(true);
				coreToast.dismiss();
				const activeNormalSettings = Object.fromEntries(
					Object.entries(activeSetting).filter(([key, _val]) => normalSettingNames.includes(key)),
				);
				const success = await saveFunc({ ...activeNormalSettings, ...activeCustomSettings }, customSettings);
				setWaiting(true);
				setLoaded(await loadFunc(loadAllSettings, customSettings));
				if (!success.ok) {
					coreToast.error(`${i18n.t("portal.components.settingsPageTSX.errorSave")}
					${success.reason ?? i18n.t("portal.components.settingsPageTSX.checkConnect")}`);
				} else {
					if (props.settingGroupName === "thirdParty") {
						coreToast.success(i18n.t("portal.components.settingsPageTSX.saveThirdParty"), {
							autoClose: 5000,
							draggable: true,
						});
					} else if (props.settingGroupName === "institute") {
						coreToast.success(i18n.t("portal.components.settingsPageTSX.saveInstitute"), {
							autoClose: 5000,
							draggable: true,
						});
					} else if (props.settingGroupName === "course") {
						coreToast.success(i18n.t("portal.components.settingsPageTSX.saveCourse"), {
							autoClose: 5000,
							draggable: true,
						});
					} else if (props.settingGroupName === "scope") {
						coreToast.success(i18n.t("portal.components.settingsPageTSX.saveScope"), {
							autoClose: 5000,
							draggable: true,
						});
					} else if (props.settingGroupName === "user") {
						coreToast.success(i18n.t("portal.components.settingsPageTSX.saveUser"), {
							autoClose: 5000,
							draggable: true,
						});
					}
				}
				setDisabled(false);
				setWaiting(false);
			})();
			return activeSetting;
		});
	}
};

export default SettingsPage;
